import { useEffect } from "react";
import { useMerchantUser } from "../hooks/useMerchantUser";
import { useOnboardingMerchant } from "../hooks/api/useOnboardingMerchant";
import { rumProvider } from "rum-provider";
import { useOneTrustPerformanceGate } from "cookie-consent";
import { useConsentGatedRum } from "merchant-core";

/**
 * Dynatrace RUM is gated by Performance consent (C0002) when OneTrust is
 * configured. Without NEXT_PUBLIC_ONETRUST_ID, MOH keeps legacy RUM loading
 * behavior.
 *
 * Consent model in MP/MOH:
 *   - The banner is suppressed in this app — consent is captured upstream
 *     (Auth0 login or zip.co marketing) and shared via the OptanonConsent
 *     cookie on .zip.co.
 *   - The gate reacts to runtime OneTrust updates (OneTrustGroupsUpdated):
 *     when Performance is off, RUM is disabled; when Performance returns,
 *     RUM initializes again.
 */
export const RumInitializer = () => {
  const { merchantId } = useMerchantUser();
  const { data: merchant } = useOnboardingMerchant();
  const { performanceAllowed } = useOneTrustPerformanceGate({
    domainScript: process.env.NEXT_PUBLIC_ONETRUST_ID,
  });
  const isEnabled = useConsentGatedRum({
    rum: rumProvider,
    performanceAllowed,
    merchantId,
  });

  // Session/context
  useEffect(() => {
    if (!isEnabled || !merchantId || !performanceAllowed) {
      return;
    }

    const businessInfo = merchant?.businessInfo;
    rumProvider.trackSession({
      merchantid: merchantId,
      merchanttier: businessInfo?.merchantTier,
      businesstype: businessInfo?.businessType,
      annualrevenue: businessInfo?.annualRevenue,
      averageordervalue: businessInfo?.averageOrderValue,
      industry: businessInfo?.industry,
      secondaryindustry: businessInfo?.secondaryIndustry,
    });
  }, [isEnabled, merchantId, merchant?.businessInfo, performanceAllowed]);

  return null;
};
